import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { client } from '../../client';

/*
 * Hand-written stand-in for DELETE /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}, live behind the API's
 * `custom-benchmarks` flag but missing from the spec the generator reads. The API soft-deletes, answering 204.
 * Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

const DELETE_BENCHMARK_URL = '/v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}';

export interface DeleteBenchmarkMutationVariables {
  workspace_id: string;
  benchmark_id: string;
}

export type DeleteBenchmarkMutation = undefined;

async function deleteBenchmark({ workspace_id, benchmark_id }: DeleteBenchmarkMutationVariables) {
  await client.delete<{ 204: undefined }, unknown, true>({
    url: DELETE_BENCHMARK_URL,
    path: { workspace_id, benchmark_id },
    throwOnError: true,
  });
  return undefined;
}

export const useDeleteBenchmarkMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<DeleteBenchmarkMutation, TError, DeleteBenchmarkMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<DeleteBenchmarkMutation, TError, DeleteBenchmarkMutationVariables, TContext>({
    mutationKey: ['DeleteBenchmark'],
    mutationFn: deleteBenchmark,
    ...options,
  });
};

useDeleteBenchmarkMutation.fetcher = (variables: DeleteBenchmarkMutationVariables) => () => deleteBenchmark(variables);
